import dayjs, { Dayjs } from "dayjs";
import { PricingYearId } from "app/shared/model";
import type { DraftMinimumStartDatesByProduct } from "app/shared/api/draftPricingYear";

export type PricingYearWindow = {
    allowedStartDate: string;
    allowedEndDate: string;
    allowedStart: Dayjs;
    allowedEnd: Dayjs;
};

const MINIMUM_START_DATE_KEY_BY_PRODUCT_TYPE: Record<
    string,
    keyof DraftMinimumStartDatesByProduct
> = {
    Course: "courses",
    Accommodation: "accommodation",
    AddOn: "addOns",
    Transfer: "transfers",
    Package: "packages",
    Fee: "fees",
};

const toIsoDate = (value?: string | Date | null): string | undefined => {
    if (!value) return undefined;

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
};

export const getPricingYearWindow = (
    pricingYear?: PricingYearId | null
): PricingYearWindow => {
    const year = pricingYear?.year ?? dayjs().year();

    const rawStart = toIsoDate(
        (pricingYear as any)?.allowedStartDate ??
        (pricingYear as any)?.pricingYearStartDate
    );

    const rawEnd = toIsoDate(
        (pricingYear as any)?.allowedEndDate ??
        (pricingYear as any)?.pricingYearEndDate
    );

    const allowedStartDate =
        rawStart ?? dayjs(`${year}-01-01`).subtract(7, "day").format("YYYY-MM-DD");

    const allowedEndDate =
        rawEnd ?? dayjs(`${year}-12-31`).add(7, "day").format("YYYY-MM-DD");

    return {
        allowedStartDate,
        allowedEndDate,
        allowedStart: dayjs(allowedStartDate),
        allowedEnd: dayjs(allowedEndDate),
    };
};

export const resolveProductAllowedStartDate = (
    productType: string,
    pricingYearWindow: PricingYearWindow,
    minimumStartDates?: DraftMinimumStartDatesByProduct | null
): string => {
    const key = MINIMUM_START_DATE_KEY_BY_PRODUCT_TYPE[productType];
    const apiDate = key ? minimumStartDates?.[key] : undefined;
    if (apiDate) return apiDate;
    return pricingYearWindow.allowedStartDate;
};

export const maxIsoDate = (
    left?: string | null,
    right?: string | null
): string => {
    const l = left ?? "";
    const r = right ?? "";

    if (!l) return r;
    if (!r) return l;

    return l > r ? l : r;
};

export const minIsoDate = (
    left?: string | null,
    right?: string | null
): string => {
    const l = left ?? "";
    const r = right ?? "";

    if (!l) return r;
    if (!r) return l;

    return l < r ? l : r;
};

export const shiftTypedIsoDateToEarliestAllowed = (
    value: string,
    min: string,
    max: string
): string => {
    if (!value) return "";
    if (value < min || value > max) return min;
    return value;
};

export const shiftPickerDateToEarliestAllowed = (
    value: Dayjs | null,
    min: string,
    max: string
): string => {
    if (!value || !value.isValid()) return "";
    const iso = value.startOf("day").format("YYYY-MM-DD");

    if (iso < min || iso > max) return min;
    return iso;
};